import { useCallback, useEffect, useMemo, useState } from "react";
import type { ImageReference } from "@metalmind-ab/mmagent-chat-ui";

interface ImageCapability {
  image_attachments_enabled: boolean;
  image_input_supported: boolean;
}

const NO_EXTRA_HEADERS: Record<string, string> = {};

/** Host chat-image API adapter. An absent capability route keeps the picker hidden. */
export function useChatImageAttachments(
  apiBaseUrl: string,
  workspaceId: string,
  getAuthHeaders: () => Promise<Record<string, string>>,
  extraHeaders: Record<string, string> = NO_EXTRA_HEADERS,
) {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let active = true;
    setEnabled(false);
    if (!workspaceId)
      return () => {
        active = false;
      };
    const load = async () => {
      try {
        const headers = {
          ...(await getAuthHeaders()),
          "X-Workspace-Id": workspaceId,
          ...extraHeaders,
        };
        const response = await fetch(`${apiBaseUrl}/chat/image-capability`, {
          headers,
        });
        if (!response.ok) return;
        const capability = (await response.json()) as ImageCapability;
        if (active) {
          setEnabled(
            capability.image_attachments_enabled === true &&
              capability.image_input_supported === true,
          );
        }
      } catch {
        // Older API deployments do not expose chat-image capability.
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [apiBaseUrl, workspaceId, getAuthHeaders, extraHeaders]);

  const uploadImage = useCallback(
    async (
      file: File,
      conversationId?: string | number | null,
    ): Promise<ImageReference> => {
      if (!enabled) throw new Error("Image attachments are unavailable");
      const form = new FormData();
      form.append("file", file);
      if (conversationId != null)
        form.append("conversation_id", String(conversationId));

      const response = await fetch(`${apiBaseUrl}/chat/images`, {
        method: "POST",
        headers: {
          ...(await getAuthHeaders()),
          "X-Workspace-Id": workspaceId,
          ...extraHeaders,
        },
        body: form,
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.detail?.message || "Image upload failed");
      }
      const { attachment_id, media_type, size_bytes } =
        (await response.json()) as ImageReference;
      return { attachment_id, media_type, size_bytes };
    },
    [apiBaseUrl, enabled, getAuthHeaders, workspaceId, extraHeaders],
  );

  return useMemo(() => ({ enabled, uploadImage }), [enabled, uploadImage]);
}
